import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "../../../lib/utils"
import { Icon } from "../icon/icon"
import type { IconName } from "../icon/icon-registry"

/**
 * Button — DESIGN.md `button-*` family.
 * - Radius is `--radius-sm` (6px) per DESIGN.md "Primary buttons carry 6px radius".
 * - Default size is `h-9` (36px) for primary CTAs; `sm` is `h-8` (32px) for secondary;
 *   `icon` defaults to 28×28 per DESIGN.md `button-icon-only`.
 * - `ai` variant uses AI Mint fill at 10% opacity per DESIGN.md `button-ai`.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-sm text-xs font-normal leading-none whitespace-nowrap transition-colors outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-40 aria-invalid:border-error aria-invalid:ring-error/20 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        // Primary CTA — DESIGN.md `button-primary`
        default: "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active",
        // Destructive — red surface
        destructive: "bg-error text-error-foreground hover:bg-error-hover focus-visible:ring-error/30",
        // Outline — quiet alternative to secondary, with primary accent on hover
        outline: "border border-input-border bg-background text-foreground hover:bg-muted",
        // Secondary — DESIGN.md `button-secondary`: white fill, gray-700 text, 1px gray-200 border, hover gray-50
        secondary: "border border-border bg-background text-foreground hover:bg-muted",
        // Ghost — invisible until hover
        ghost: "text-foreground hover:bg-muted hover:text-foreground",
        // Link
        link: "text-primary leading-normal underline-offset-4 hover:underline",
        // AI — DESIGN.md `button-ai`: AI Mint fill at 10% opacity, AI Mint text
        ai: "bg-[var(--ai-fill-medium)] text-[var(--ai-text)] hover:bg-[var(--ai-fill-chip)]",
      },
      size: {
        // DESIGN.md primary CTA — 36px tall, 12px h-padding
        default: "h-9 px-3 has-[>svg]:px-2.5",
        xs: "h-6 gap-1 px-2 text-[10px] has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        // DESIGN.md secondary — 32px tall
        sm: "h-8 gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 px-5 has-[>svg]:px-4",
        pill: "h-9 px-6 rounded-full has-[>svg]:px-4",
        "pill-lg": "h-10 px-8 rounded-full has-[>svg]:px-6",
        // DESIGN.md `button-icon-only` — 28×28 (h-7)
        icon: "size-7 [&_svg:not([class*='size-'])]:size-4",
        "icon-xs": "size-6 [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-7 [&_svg:not([class*='size-'])]:size-4",
        "icon-lg": "size-8",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const buttonIconSize: Record<
  NonNullable<VariantProps<typeof buttonVariants>["size"]>,
  "xs" | "sm" | "md" | "lg"
> = {
  default: "md",
  xs: "xs",
  sm: "sm",
  lg: "lg",
  pill: "md",
  "pill-lg": "lg",
  icon: "md",
  "icon-xs": "xs",
  "icon-sm": "sm",
  "icon-lg": "lg",
}

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  iconStart,
  iconEnd,
  children,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
    /** Renders a registered icon before `children` (ignored when `asChild` is true — compose `<Icon />` yourself) */
    iconStart?: IconName
    /** Renders a registered icon after `children` (ignored when `asChild` is true) */
    iconEnd?: IconName
  }) {
  const Comp = asChild ? Slot.Root : "button"
  const iconSize = buttonIconSize[size ?? "default"]

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      {asChild ? (
        children
      ) : (
        <>
          {iconStart ? <Icon name={iconStart} color="inherit" size={iconSize} /> : null}
          {children}
          {iconEnd ? <Icon name={iconEnd} color="inherit" size={iconSize} /> : null}
        </>
      )}
    </Comp>
  )
}

export { Button, buttonVariants }
